/**
 * Cambio codice CER RIFIUTATO, senza possibilità di conferma (#116).
 *
 * Due casi, entrambi non superabili dall'utente:
 *  - `cer_bloccato_conferimenti_fatturati`: il CER è finito nella descrizione
 *    di righe già fatturate;
 *  - `cer_saldo_insufficiente`: i kg non sono più a saldo sotto il codice
 *    attuale perché il materiale è già stato scaricato dall'area.
 *
 * NON è una variante della modale di conferma e non deve diventarlo: qui non
 * esiste un rilancio possibile, l'unica azione è chiudere. Il footer ha un
 * solo pulsante proprio per non suggerire che ci sia una via d'uscita.
 */
import { XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Modal from './ui/Modal';
import { type CerBloccatoDetail } from '../lib/cerLedger';
import { fmtKg } from '../lib/saldo';

interface Props {
  detail: CerBloccatoDetail;
  onClose: () => void;
}

export default function CerBloccatoModal({ detail, onClose }: Props) {
  const { t } = useTranslation();
  const perSaldo = detail.code === 'cer_saldo_insufficiente';

  return (
    <Modal
      title={perSaldo
        ? t('richieste.cer_bloccato.titolo_saldo')
        : t('richieste.cer_bloccato.titolo_fatturati')}
      subtitle={t('richieste.cer_bloccato.sottotitolo')}
      onClose={onClose}
      maxWidth="max-w-xl"
      testid="cer-bloccato-modal"
      footer={
        <button
          type="button"
          onClick={onClose}
          data-testid="cer-bloccato-chiudi"
          className="px-3 py-1.5 text-xs font-medium text-ink-mid border border-stone-border rounded-md hover:bg-white transition-colors"
        >
          {t('common.azioni.chiudi')}
        </button>
      }
    >
      <div className="flex gap-2.5 text-sm text-ink-dark">
        <XCircle size={16} className="text-accent-danger flex-shrink-0 mt-0.5" />
        <p>{perSaldo
          ? t('richieste.cer_bloccato.testo_saldo')
          : t('richieste.cer_bloccato.testo_fatturati')}</p>
      </div>

      <div>
        <p className="text-xs text-ink-soft mb-1.5">
          {t('richieste.cer_bloccato.conferimenti_coinvolti', { count: detail.conferimenti.length })}
        </p>
        <ul className="border border-stone-mid rounded-md divide-y divide-stone-mid max-h-52 overflow-y-auto">
          {detail.conferimenti.map((c) => (
            <li key={`${c.id}-${c.area_id}-${c.materiale}`} className="px-3 py-2 text-sm">
              <div className="flex items-center gap-3">
                <span className="font-mono text-ink-dark flex-1">{c.numero ?? '—'}</span>
                <span className="text-xs text-ink-mid">
                  {t('richieste.propagazione_ledger.area', { area: c.area_codice ?? '—' })}
                </span>
                <span className="text-ink-dark tabular-nums">{fmtKg(c.kg)} kg</span>
              </div>
              {perSaldo && (
                <div className="flex items-center gap-4 mt-1 text-xs text-ink-soft">
                  <span>
                    {t('richieste.cer_bloccato.col_saldo_area')}: <span className="tabular-nums">{fmtKg(c.saldo_area_kg ?? 0)} kg</span>
                  </span>
                  <span className="text-accent-danger">
                    {t('richieste.cer_bloccato.col_mancanti')}: <span className="tabular-nums">{fmtKg(c.mancanti_kg ?? 0)} kg</span>
                  </span>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}
